import { performRequest, methodType } from "./_apiConfig";
import { promptPdfPassword } from "../utils/pdf_password_prompt";

// Backend signalling codes for password-protected PDFs (see documents/pdf_utils).
const PASSWORD_CODES = ["password_required", "password_invalid"];

// File fields used across the various upload endpoints (for the prompt label).
const PROOF_FIELDS = ["file", "bank_account_proof", "proof_files", "attachments"];

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

// The API wraps DRF responses via CustomRenderer as
// `{ success:false, error:{ code, ... }, status }`, while a few views return a
// flat JsonResponse `{ code, message }`. DRF ValidationError also list-wraps
// values. Read the code/message from whichever shape applies.
const extractCode = (body) => firstOf(body?.code) || firstOf(body?.error?.code);

const extractFileName = (body) =>
  firstOf(body?.file_name) || firstOf(body?.error?.file_name);

const extractMessage = (body) =>
  firstOf(body?.error?.error) ||
  firstOf(body?.error?.message) ||
  (typeof body?.error === "string" ? body.error : null) ||
  firstOf(body?.message) ||
  "Upload failed";

const getFileName = (formData) => {
  if (typeof formData?.get !== "function") return undefined;
  for (const key of PROOF_FIELDS) {
    const value = formData.get(key);
    if (value && value.name) return value.name;
  }
  return undefined;
};

const defaultPromptForPassword = (code, fileName) => promptPdfPassword(code, fileName);

/**
 * Send a multipart `formData` upload. If the backend reports the file is a
 * password-protected PDF, prompt the user for the password and retry so the
 * file is unlocked and stored decrypted server-side. No password is persisted.
 *
 * Returns the raw success body, or `{ success: false, error }` on failure —
 * matching the shape existing callers already handle.
 */
export const postFormWithPasswordPrompt = async (
  endpoint,
  formData,
  {
    method = methodType.POST,
    requiresAuth = true,
    promptForPassword = defaultPromptForPassword,
  } = {}
) => {
  const attempt = async (password) => {
    if (password) {
      formData.set("pdf_password", password);
    }
    try {
      return await performRequest(method, endpoint, formData, requiresAuth, true);
    } catch (err) {
      const code = extractCode(err);
      if (PASSWORD_CODES.includes(code)) {
        const entered = await promptForPassword(code, getFileName(formData));
        if (entered == null || String(entered).trim() === "") {
          return {
            success: false,
            error: "A password is required to upload this protected PDF.",
          };
        }
        return attempt(String(entered).trim());
      }
      return { success: false, error: extractMessage(err) };
    }
  };

  return attempt(null);
};

/**
 * Like postFormWithPasswordPrompt, but for a single request carrying MULTIPLE
 * files (e.g. support-ticket attachments) that may each have a different
 * password. The backend reports which file needs a password (`file_name`); we
 * prompt for that file, accumulate a filename->password map, send it as
 * `pdf_passwords`, and retry until every protected file is unlocked.
 *
 * Returns the raw success body, or `{ success: false, error }` on failure.
 */
export const postFormWithPerFilePasswordPrompt = async (
  endpoint,
  formData,
  {
    method = methodType.POST,
    requiresAuth = true,
    promptForPassword = defaultPromptForPassword,
  } = {}
) => {
  const passwords = {}; // filename -> password

  const attempt = async () => {
    formData.set("pdf_passwords", JSON.stringify(passwords));
    try {
      return await performRequest(method, endpoint, formData, requiresAuth, true);
    } catch (err) {
      const code = extractCode(err);
      if (PASSWORD_CODES.includes(code)) {
        const fileName =
          extractFileName(err) || getFileName(formData) || "this PDF";
        const entered = await promptForPassword(code, fileName);
        if (entered == null || String(entered).trim() === "") {
          return {
            success: false,
            error: "A password is required to upload this protected PDF.",
          };
        }
        passwords[fileName] = String(entered).trim();
        return attempt();
      }
      return { success: false, error: extractMessage(err) };
    }
  };

  return attempt();
};
